import { DiscardModal } from '@/components/Modals/DiscardModal'
import { useRBACPermissions } from '@/contexts/useRBACPermissions'
import { Accordion } from '@chakra-ui/react'
import { useCallback, useState } from 'react'
import { OrgFeaturesPulse } from './OrgFeatures/OrgFeaturesPulse'
import { OrgFeaturesSso } from './OrgFeatures/OrgFeaturesSso'

export function FeaturesAccordion() {
  // RBAC permissions
  const { hasAdminRights, hasOperatorRights, hasReaderRights } =
    useRBACPermissions()

  // Permission logic:
  // - No rights: all accordions closed and disabled (canRead = false)
  // - Reader rights only: view only, cannot edit (canRead = true, canEdit = false)
  // - Operator/Admin rights: can read and edit features (canRead = true, canEdit = true)
  const canRead = hasAdminRights() || hasOperatorRights() || hasReaderRights()
  const canEdit = hasAdminRights() || hasOperatorRights()

  // Accordion and modal state
  const [openValue, setOpenValue] = useState<string | undefined>(undefined)
  const [hasChanges, setHasChanges] = useState(false)
  const [showDiscardModal, setShowDiscardModal] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  // Reset trigger to notify child components
  const [resetTrigger, setResetTrigger] = useState(false)

  // Handle accordion section change
  const handleAccordionChange = useCallback(
    (value: string | undefined) => {
      // Prevent accordion changes if user cannot read
      if (!canRead) {
        return
      }

      if (hasChanges) {
        setShowDiscardModal(true)
        return
      }
      setOpenValue(value)
    },
    [canRead, hasChanges],
  )

  // Handle changes in child components
  const handleHasChangesChange = useCallback((newHasChanges: boolean) => {
    setHasChanges(newHasChanges)
  }, [])

  // Handle cancel button in OrgFeaturesSso
  const handleCancel = useCallback(() => {
    setShowCancelModal(true)
  }, [])

  // Handle confirmation of cancel action
  const confirmCancel = useCallback(() => {
    setShowCancelModal(false)
    setResetTrigger((prev) => !prev)
  }, [])

  // Handle modal for discarding changes when switching sections
  const handleDiscardModalClose = useCallback((discard: boolean) => {
    if (discard) {
      setOpenValue(undefined)
      setResetTrigger((prev) => !prev)
    }
    setShowDiscardModal(false)
  }, [])

  return (
    <>
      <Accordion.Root
        collapsible={canRead}
        value={openValue ? [openValue] : []}
        onValueChange={(details) => {
          const newValue =
            details.value.length > 0 ? details.value[0] : undefined
          handleAccordionChange(newValue)
        }}
        width="100%"
      >
        <OrgFeaturesSso
          onHasChangesChange={handleHasChangesChange}
          onCancel={handleCancel}
          resetChanges={resetTrigger}
          canRead={canRead}
          canEdit={canEdit}
        />
        <OrgFeaturesPulse
          onHasChangesChange={handleHasChangesChange}
          onCancel={handleCancel}
          resetChanges={resetTrigger}
          canRead={canRead}
          canEdit={canEdit}
        />
      </Accordion.Root>
      <DiscardModal
        isOpen={showDiscardModal}
        onClose={() => handleDiscardModalClose(false)}
        onConfirmDiscard={() => handleDiscardModalClose(true)}
      />
      <DiscardModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirmDiscard={confirmCancel}
      />
    </>
  )
}
